import { useNavigate } from "react-router-dom";

import { Button, Form, Input, InputRef, Layout, Spin, message } from "antd";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";

import { rumService } from "@/components/DatadogRumSectionBoundary/rum-service";
import DowntimeModal from "@/components/DowntimeModal";
import { API_ROUTES, IMAGES, ROUTES_URL, SUPPORTED_COUNTRIES } from "@/constant";
import { getAuthBaseUrl, getTenantDomain } from "@/constant/singpassConstants";
import useCooldown from "@/hooks/useCooldown";
import styles from "@/pages/Login/Login.module.css";
import {
  SingpassLogin,
  customerResendOTP,
  customerVerifyEmail,
  customerVerifyOTP,
  loginUser,
  loginUserMFA,
  submitLoginOtp,
} from "@/services/Auth/AuthApi";
import { useAuthStore } from "@/stores/authStore";
import { useTenantStore } from "@/stores/tenantStore";
import { LoginPayloadInterface } from "@/types/authService";

const Login: React.FC = () => {
  const { cooldown, isCooldownActive, startCooldown, executeWithCooldown } = useCooldown();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [passwordScreen, setPasswordScreen] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [infoAlert, setInfoAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const COUNTRY = useTenantStore((state) => state.country);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpValues, setOtpValues] = useState(["", "", "", ""]);
  const [downtimeWindow, setDowntimeWindow] = useState<{
    startTime: string;
    endTime: string;
  } | null>(null);
  const inputRefs = useRef<(InputRef | null)[]>([]);

  const releaseTag = process.env.GIT_RELEASE_TAG || "";

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (params.get("downtime") === "true") {
      setDowntimeWindow({
        startTime: params.get("start_time") || "",
        endTime: params.get("end_time") || "",
      });
    }
  }, []);

  // const handleCloseModal = (): void => {
  //   setOtpModal(false);
  // };

  // const closeLoginService = (): void => {
  //   setServiceModal(false);
  // };

  const handleSubmitOTP = async (otp: string): Promise<void> => {
    try {
      setOtpLoading(true);

      if (!customerId) {
        message.error("Customer ID missing");
        return;
      }

      const response = await customerVerifyOTP({
        otp,
        customer_id: customerId,
      });

      const { token, customer, tenant, refresh_token } = response.data;

      const isMalaysia = tenant?.country === "Malaysia";
      const isSingapore = tenant?.country === "Singapore";

      const currency = isSingapore ? "S$" : isMalaysia ? "RM" : null;

      useAuthStore.getState().updateState({
        customer,
        token,
        refresh_token,
        isAuthenticated: true,
        currency,
        isMalaysia,
        isSingapore,
        isTCAccepted: customer.is_accepted_tnc,
      });

      message.success("Logged in successfully");

      navigate(ROUTES_URL.DASHBOARD);
    } catch (error: unknown) {
      rumService.trackLoginFailure("otp", error);
      if (axios.isAxiosError<{ message: string }>(error)) {
        message.error(error?.response?.data?.message || "Invalid OTP");
      }
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOTP = async (): Promise<void> => {
    try {
      await executeWithCooldown(async () => {
        if (!customerId) {
          message.error("Customer ID missing");
          return;
        }

        const response = await customerResendOTP({
          customer_id: customerId,
        });

        message.success(response?.message || response?.data?.message || "OTP re-sent successfully");
      });
    } catch (error: unknown) {
      console.error(error);
      message.error("Failed to resend OTP");
    }
  };

  // Singpass final login after direction
  const handleLogin = async (values: LoginPayloadInterface): Promise<void> => {
    try {
      setLoading(true);
      const { data } = await loginUser(values);

      const { token, customer, tenant } = data.data;
      const isMalaysia = tenant?.country === "Malaysia";
      const isSingapore = tenant?.country === "Singapore";
      const currency = isSingapore ? "S$" : isMalaysia ? "RM" : null;

      useAuthStore.getState().updateState({
        customer,
        token,
        isAuthenticated: true,
        currency,
        isMalaysia,
        isSingapore,
        isTCAccepted: customer.is_accepted_tnc,
      });

      navigate(ROUTES_URL.DASHBOARD);
    } catch (error) {
      console.error("Login error:", error);
    } finally {
      setLoading(false);
    }
  };

  const loginDashbaord = async () => {
    navigate(ROUTES_URL.DASHBOARD);
  };

  const handleSingpassLogin = async (): Promise<void> => {
    try {
      setLoading(true);
      const url = new URL(`${await getAuthBaseUrl()}${API_ROUTES.SINGPASS_LOGIN_URL}`);

      url.searchParams.set("appType", "login");
      url.searchParams.set("platform", "web");
      url.searchParams.set("x-tenant-domain", getTenantDomain());

      window.location.href = url.toString();
    } catch (error) {
      console.error("Singpass login initiation failed:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const platform = params.get("platform");

    if (code && state && platform == "web") {
      setLoading(true);
      void validateSingpass(code, state);
    } else if (code && state && platform == "app") {
      window.location.href = `moneymaxcustomer://login?code=${code}&state=${state}&platform=app`;
    }
  }, [navigate]);

  const validateSingpass = async (code: string, state: string): Promise<void> => {
    try {
      const data = await SingpassLogin(code, state);
      const { token, refresh_token, customer, tenant } = data;
      const isMalaysia = tenant?.country === "Malaysia";
      const isSingapore = tenant?.country === "Singapore";
      const currency = isSingapore ? "S$" : isMalaysia ? "RM" : null;

      useAuthStore.getState().updateState({
        customer,
        token,
        refresh_token,
        isAuthenticated: true,
        currency,
        isMalaysia,
        isSingapore,
        isTCAccepted: customer.is_accepted_tnc,
      });

      navigate(ROUTES_URL.DASHBOARD);
    } catch (error) {
      rumService.trackLoginFailure("singpass", error);
      setLoading(false);
      console.error("SingPass validation failed:", error);
      window.history.replaceState({}, "", location.pathname);
    }
  };

  const handlepasswordLogin = async (values: { password: string }): Promise<void> => {
    try {
      setLoading(true);

      const response = await loginUserMFA({
        password: values.password,
        user_id: userEmail,
      });

      const {
        isFirstLogin,
        customer_id,
        token,
        refresh_token,
        customer,
        tenant,
        message: responseMessage,
      } = response;

      const isMalaysia = tenant?.country === "Malaysia";
      const isSingapore = tenant?.country === "Singapore";

      const currency = isSingapore ? "S$" : isMalaysia ? "RM" : null;

      message.success(responseMessage);

      if (isFirstLogin) {
        useAuthStore.getState().updateState({
          customer,
          token,
          refresh_token,
          hashKey: values.password,
          email: userEmail,
          isAuthenticated: false,
          isTCAccepted: false,
          isMalaysia,
          isSingapore,
          currency,
        });

        navigate(ROUTES_URL.RESET_PASSWORD);

        return;
      }

      setCustomerId(customer_id);

      setOtpValues(["", "", "", ""]);
      startCooldown();
      setMfaRequired(true);
    } catch (error: unknown) {
      rumService.trackLoginFailure("password", error);
      if (axios.isAxiosError<{ message: string }>(error)) {
        message.error(error?.response?.data?.message || "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (value: string, index: number): void => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otpValues];

    newOtp[index] = value;

    setOtpValues(newOtp);

    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number): void => {
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (e.key === "Enter" && otpValues.every((digit) => digit !== "")) {
      void handleSubmitOTP(otpValues.join(""));
    }
  };

  useEffect(() => {
    if (mfaRequired) {
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 0);
    }
  }, [mfaRequired]);

  const handleCheckEmail = async (values: { email: string }): Promise<void> => {
    setLoading(true);
    setErrorMessage("");
    try {
      const { data } = await customerVerifyEmail({ email: values.email });
      if (data.is_exist && data.is_eservice_enabled !== false) {
        if (data.is_first_login) {
          setInfoAlert(true);
        } else {
          setInfoAlert(false);
        }
        setUserEmail(values.email);
        setPasswordScreen(true);
      } else {
        setInfoAlert(false);
        setErrorMessage(data.message || "");
      }
    } catch (error: unknown) {
      console.error("Login error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Spin spinning={loading} className={styles.spinWrapper}>
      <div className={styles.gradientBg}>
        <div className={styles.loginWrapper}>
          <div className={styles.formContainer}>
            <div className={styles.logo}>
              <img
                src={IMAGES.LOGO}
                alt="Logo"
                className={styles.logoImage}
                data-dd-privacy="allow"
              />
            </div>

            <h2 className={styles.loginHeading} data-dd-privacy="allow">
              Login
            </h2>
            {mfaRequired ? (
              <>
                <p className={styles.otpLoginSubTitle}>
                  Enter the OTP sent to
                  <span className={styles.otpEmail} data-dd-privacy="mask">
                    {userEmail}
                  </span>
                </p>

                <div className={styles.otpContainer}>
                  {otpValues.map((digit, index) => (
                    <Input
                      key={index}
                      value={digit}
                      maxLength={1}
                      className={styles.otpInput}
                      data-dd-privacy="mask"
                      ref={(el) => (inputRefs.current[index] = el)}
                      onChange={(e) => handleOtpChange(e.target.value, index)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                    />
                  ))}
                </div>

                <div className={styles.remember}>
                  <span
                    onClick={() => {
                      setMfaRequired(false);
                      setOtpValues(["", "", "", ""]);
                      setPasswordScreen(false);
                      setErrorMessage("");
                      setInfoAlert(false);
                    }}
                    className={styles.forgotPassword}
                  >
                    <img src={IMAGES.LOGIN_RIGHT_ARROW} alt="back to login" /> Back to Login
                  </span>

                  <span
                    onClick={() => {
                      if (!isCooldownActive) {
                        void handleResendOTP();
                      }
                    }}
                    className={`${styles.forgotPassword} ${
                      isCooldownActive ? styles.disabledResend : ""
                    }`}
                  >
                    {isCooldownActive ? `Resend OTP (${cooldown}s)` : "Resend OTP"}
                  </span>
                </div>

                <Button
                  block
                  loading={otpLoading}
                  disabled={otpValues.some((d) => d === "")}
                  className={styles.verifyOtpButton}
                  onClick={() => handleSubmitOTP(otpValues.join(""))}
                >
                  Verify OTP
                </Button>
              </>
            ) : passwordScreen ? (
              <>
                <p className={styles.loginSubTitle} data-dd-privacy="allow">
                  Enter your password to access your account.
                </p>

                {infoAlert && (
                  <div className="h-[47px] bg-[#CDE6F7] rounded-[5px] flex items-center px-2 gap-x-3 mb-6">
                    <img src={IMAGES.EMAIL_FIRST_LOGIN} />
                    <span className="text-[#0082CA] text-[11px]">
                      An one time password has been sent to your email, please use that to proceed
                    </span>
                  </div>
                )}

                <Form form={form} name="login" layout="vertical" onFinish={handlepasswordLogin}>
                  <Form.Item
                    name="password"
                    className={styles.password}
                    label="Password"
                    required={false}
                    rules={[{ required: true, message: "Password is required" }]}
                  >
                    <Input.Password
                      placeholder="Enter your password"
                      data-dd-privacy="mask"
                      prefix={<img src={IMAGES.PASSWORD_ICON} className={styles.passwordIcon} />}
                    />
                  </Form.Item>

                  {!infoAlert && (
                    <Form.Item>
                      <div className={styles.RememberMe}>
                        <span
                          onClick={() => {
                            setPasswordScreen(false);
                            setErrorMessage("");
                            setInfoAlert(false);
                          }}
                          className={styles.forgotPassword}
                        >
                          <img src={IMAGES.LOGIN_RIGHT_ARROW} alt="back to login" />{" "}
                          <span data-dd-privacy="allow">Back to Login</span>
                        </span>
                        <span
                          onClick={() => navigate(ROUTES_URL.FORGOT_PASSWORD)}
                          className={styles.forgotPassword}
                        >
                          <span data-dd-privacy="allow">Forgot password?</span>
                        </span>
                      </div>
                    </Form.Item>
                  )}

                  <Form.Item className={styles.login}>
                    <Button
                      block
                      htmlType="submit"
                      loading={loading}
                      className={styles.submitButton}
                    >
                      <span data-dd-privacy="allow">Login</span>
                    </Button>
                  </Form.Item>
                </Form>
              </>
            ) : (
              <>
                <p className={styles.loginSubTitle} data-dd-privacy="allow">
                  Enter your email or login ID to access your account.
                </p>

                {errorMessage && (
                  <div className="h-[47px] bg-[#F3CACA] rounded-[5px] flex items-center px-2 gap-x-3 mb-6">
                    <img src={IMAGES.EMAIL_NOT_FOUND} />
                    <span className="text-danger-100 text-[11px]">{errorMessage}</span>
                  </div>
                )}

                <Form form={form} name="login" layout="vertical" onFinish={handleCheckEmail}>
                  <Form.Item
                    name="email"
                    className={styles.userId}
                    label={<span data-dd-privacy="allow">Email or Login ID</span>}
                    required={false}
                    rules={[{ required: true, message: "Please enter your email or login ID" }]}
                  >
                    <Input
                      placeholder="Enter your Email or Login ID"
                      data-dd-privacy="mask"
                      prefix={<img src={IMAGES.USER_ICON} className={styles.userIcon} />}
                    />
                  </Form.Item>

                  <Form.Item className={styles.login}>
                    <Button
                      block
                      htmlType="submit"
                      loading={loading}
                      className={styles.submitButton}
                    >
                      <span data-dd-privacy="allow">Next</span>
                    </Button>
                  </Form.Item>

                  {COUNTRY === SUPPORTED_COUNTRIES.SG && (
                    <>
                      <div className={styles.or}>
                        <div className={styles.customLine} />
                        <span className={styles.orText}>Or</span>
                        <div className={styles.customLine} />
                      </div>

                      <Form.Item className={styles.singpassContainer}>
                        <Button
                          htmlType="button"
                          className={styles.dangerButton}
                          onClick={handleSingpassLogin}
                        >
                          <span className={styles.singpassContent}>
                            Log in with
                            <img
                              src={IMAGES.SINGPASS}
                              alt="singpass"
                              className={styles.singpassLogo}
                            />
                          </span>
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form>

                <div className={styles.footer}>
                  By signing up, you agree to our <br />
                  <a href={ROUTES_URL.TERMS} className={styles.customTag}>
                    <span data-dd-privacy="allow">Terms &amp; Conditions</span>
                  </a>{" "}
                  and{" "}
                  <a href={ROUTES_URL.PRIVACY_POLICY} className={styles.customTag}>
                    <span data-dd-privacy="allow">Privacy Policy</span>
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
        {/* {otpModal ? (
          <OtpModal
            open={otpModal}
            onClose={handleCloseModal}
            onSubmit={handleSubmitOTP}
            onSubmitResend={handleResendOTP}
          />
        ) : null} */}

        {releaseTag ? (
          <Layout.Footer className={styles.appFooter}>
            <div className={styles.appFooterText}>{releaseTag}</div>
          </Layout.Footer>
        ) : null}

        {downtimeWindow ? (
          <DowntimeModal
            open={Boolean(downtimeWindow)}
            startTime={downtimeWindow.startTime}
            endTime={downtimeWindow.endTime}
            onClose={() => {
              setDowntimeWindow(null);
              window.history.replaceState({}, "", ROUTES_URL.LOGIN);
            }}
          />
        ) : null}
      </div>
    </Spin>
  );
};

export default Login;
